/**
 * PATH: packages/email-hub/src/feature/components/rich-text/CardPicker.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-CARDPICKER-001
 * PURPOSE: Search-and-pick modal for embedding a Kanban card chip into the
 *          email body. Hits GET /api/kanban/search/?types=card and returns a
 *          normalised KanbanCardAttrs (with an absolute deep-link URL).
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ClipboardList, Loader2, Search, X } from 'lucide-react'
import { emailApiFetch } from '../../lib/api'
import type { KanbanCardAttrs } from './kanbanCardNode'

interface CardSearchRow {
  type: string
  id: string
  title: string
  subtitle?: string
  status?: string
  url?: string
}

function toAttrs(row: CardSearchRow): KanbanCardAttrs {
  let url = ''
  try {
    url = row.url ? new URL(row.url, window.location.origin).href : ''
  } catch {
    url = row.url || ''
  }
  return {
    cardId: row.id,
    title: row.title || 'Card',
    subtitle: row.subtitle || '',
    status: row.status || '',
    url,
  }
}

export function CardPicker({ open, onClose, onPick }: {
  open: boolean
  onClose: () => void
  onPick: (attrs: KanbanCardAttrs) => void
}) {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<CardSearchRow[]>([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQuery(''); setRows([]); setErr(null)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) { setRows([]); setLoading(false); return }
    setLoading(true)
    const ctrl = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        const res = await emailApiFetch(
          `/api/kanban/search/?types=card&limit=8&q=${encodeURIComponent(q)}`,
          { signal: ctrl.signal },
        )
        const data = await res.json().catch(() => ({ results: [] }))
        if (ctrl.signal.aborted) return
        setRows(((data.results as CardSearchRow[]) || []).filter((r) => r.type === 'card'))
        setErr(null)
      } catch (e) {
        if (!ctrl.signal.aborted) setErr(e instanceof Error ? e.message : 'Search failed')
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, 200)
    return () => { ctrl.abort(); window.clearTimeout(timer) }
  }, [query, open])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-black/40 pt-[12vh]"
      onMouseDown={onClose} role="dialog" aria-modal="true" aria-label="Insert Kanban card">
      <div className="w-full max-w-lg rounded-xl border border-border bg-popover shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search cards by title…" aria-label="Search cards"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <button type="button" onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {err && <div className="px-3 py-3 text-sm text-destructive">{err}</div>}
          {!err && query.trim().length < 2 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">Type at least 2 characters to search.</div>
          )}
          {!err && !loading && query.trim().length >= 2 && rows.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No cards found.</div>
          )}
          {rows.map((row) => (
            <button key={row.id} type="button" onClick={() => { onPick(toAttrs(row)); onClose() }}
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-accent">
              <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">{row.title}</div>
                {(row.status || row.subtitle) && (
                  <div className="truncate text-xs text-muted-foreground">{[row.status, row.subtitle].filter(Boolean).join(' \u00B7 ')}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
